import { useApiMutation } from "@gladis/api-client";
import { formatEur, type PropertyDetailDto } from "@gladis/shared";

/** One row of the detail payload — what the edit dialog is handed. */
export type MortgagePartDto = PropertyDetailDto["mortgageParts"][number];
import { ActionMenu, Button, EmptyState, Table } from "@gladis/ui";
import { formatDayAndMonth, useT } from "@gladis/ui/i18n";
import { useState, type ReactNode } from "react";

import { PROPERTIES_KEY } from "./PropertyList.js";
import { usePropertyDetail } from "./ValuationHistory.js";

/*
 * THE CONSTANT HOLDS KEYS, NOT SENTENCES — feature 021.
 *
 * A module-scope `t("…")` is evaluated once, when the module is first imported, which freezes the
 * label in whichever language happened to be loaded at that moment. It renders correctly for whoever
 * opens the application and wrongly for whoever switches, and nothing about it looks wrong.
 *
 * These three are the same word in both languages — see the note in `en.ts` — so the bug would have
 * been invisible here in particular. That is the reason to fix the SHAPE rather than rely on the
 * values: the next person to add a fourth type will not know that.
 */
const TYPE_LABEL_KEYS = {
  annuity: "prop.mortgage.annuity",
  linear: "prop.mortgage.linear",
  interest_only: "prop.mortgage.interestOnly",
} as const;

/**
 * A property's mortgage, part by part, with what is still owed on each.
 *
 * ============================================================================
 * NOTHING ON THIS SCREEN WAS TYPED IN BY ANYONE
 * ============================================================================
 *
 * `outstandingBalance` and `monthlyInterest` are derived from six parameters and today's date, on
 * the server, every time this loads. There is no field for either anywhere in the application.
 *
 * `paymentsMade` is shown beside them ON PURPOSE, and it is the only column here that is not money.
 * These are the product's only figures nobody entered, so a household that wants to check them
 * against their bank's amortisation table needs the month index to do it — and being able to check
 * is what makes a derived number trustworthy rather than merely asserted.
 *
 * The monthly figure is labelled INTEREST, never "payment". Repaying principal is not a cost; it is
 * money moving into equity, already reflected by the falling balance. Labelling it loosely here is
 * how it would end up double-counted in feature 009.
 */
/**
 * NO ADD CONTROL OF ITS OWN, since the property page gathered its actions into the title row.
 *
 * It had two — one in the empty state and one above the table — which is how a page ends up with its
 * controls at whatever height a section happens to start. "Add a mortgage part" now sits with every
 * other action this property has, and the empty state below says what to do rather than offering a
 * second way to do it.
 */
export function MortgagePartList({
  propertyId,
  onEdit,
  nextDue,
}: {
  propertyId: string;
  /** The page owns the dialog, so the row only says which part was chosen. */
  onEdit: (part: MortgagePartDto) => void;
  /**
   * Each part's next due date, keyed by part id (feature 024). It is the expenses schedule's figure, handed in
   * by the page; the column is omitted when this is absent, as the expenses table omits its own.
   */
  nextDue?: Readonly<Record<string, string | null>> | undefined;
}): ReactNode {
  const t = useT();
  const detail = usePropertyDetail(propertyId);
  const [showArchived, setShowArchived] = useState(false);

  const archive = useApiMutation<void, { partId: string; verb: "archive" | "unarchive" }>(
    (client, { partId, verb }) =>
      client.post<void>(`/properties/${propertyId}/mortgage-parts/${partId}/${verb}`, {}),
    { invalidates: [PROPERTIES_KEY] },
  );

  const parts = detail.data?.mortgageParts ?? [];
  const active = parts.filter((part) => part.archivedAt === null);
  const archived = parts.filter((part) => part.archivedAt !== null);

  const owed = active.reduce((total, part) => total + part.outstandingBalance, 0);

  if (parts.length === 0) {
    return <EmptyState>{t("prop.mortgage.empty")}</EmptyState>;
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-muted text-sm">
        Still owed across {active.length} active {active.length === 1 ? "part" : "parts"}:{" "}
        <span className="text-ink font-medium tabular-nums">{formatEur(owed)}</span>
      </p>

      <Table>
        <Table.Head>
          <Table.Row>
            <Table.HeaderCell>{t("prop.mortgage.colPart")}</Table.HeaderCell>
            <Table.HeaderCell numeric>{t("prop.mortgage.colBorrowed")}</Table.HeaderCell>
            <Table.HeaderCell numeric>{t("prop.mortgage.colRate")}</Table.HeaderCell>
            <Table.HeaderCell numeric>{t("prop.mortgage.colPaymentsMade")}</Table.HeaderCell>
            <Table.HeaderCell numeric>{t("prop.mortgage.colStillOwed")}</Table.HeaderCell>
            <Table.HeaderCell numeric>{t("prop.mortgage.colInterestPerMonth")}</Table.HeaderCell>
            {nextDue === undefined ? null : (
              <Table.HeaderCell>{t("prop.mortgage.colNextDue")}</Table.HeaderCell>
            )}
            <Table.HeaderCell> </Table.HeaderCell>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {(showArchived ? parts : active).map((part) => (
            <Table.Row key={part.id}>
              <Table.Cell>
                <span className="flex flex-col">
                  <span className="font-medium">
                    {part.name ?? t(TYPE_LABEL_KEYS[part.mortgageType])}
                  </span>
                  <span className="text-sm text-muted">
                    {t(TYPE_LABEL_KEYS[part.mortgageType])}
                    {part.lender ? ` · ${part.lender}` : ""}
                    {part.archivedAt ? " · archived" : ""}
                  </span>
                </span>
              </Table.Cell>
              <Table.Cell numeric>{formatEur(part.originalPrincipal)}</Table.Cell>
              <Table.Cell numeric>{part.interestRate.toFixed(2)}%</Table.Cell>
              {/* Negative for a part that has not started — which is why it is not clamped. */}
              <Table.Cell numeric>{part.paymentsMade}</Table.Cell>
              <Table.Cell numeric>{formatEur(part.outstandingBalance)}</Table.Cell>
              <Table.Cell numeric>{formatEur(part.monthlyInterest)}</Table.Cell>
              {nextDue === undefined ? null : (
                <Table.Cell>
                  {/* An archived part is off the schedule (FR-009), so it has no next payment either. */}
                  {part.archivedAt === null && nextDue[part.id] ? (
                    <span className="tabular-nums">
                      {formatDayAndMonth(nextDue[part.id] ?? "")}
                    </span>
                  ) : (
                    <span className="text-muted" aria-label={t("prop.mortgage.noDueDate")}>
                      &mdash;
                    </span>
                  )}
                </Table.Cell>
              )}
              <Table.Cell>
                {/*
                 * BEHIND ONE CONTROL, the way income's and investments' rows do it. A bare button in
                 * the last cell made that column as wide as its longest label and put the row's
                 * controls in competition with its figures — and there are two actions now, not one.
                 */}
                <ActionMenu
                  label={`Actions for ${part.name ?? t(TYPE_LABEL_KEYS[part.mortgageType])}`}
                  items={[
                    {
                      label: t("prop.mortgage.edit"),
                      /*
                       * An archived part is read-only. Editing one would change a figure that no
                       * longer contributes to any total, which is a change nobody can see.
                       */
                      disabled: part.archivedAt !== null,
                      onSelect: () => {
                        onEdit(part);
                      },
                    },
                    {
                      label:
                        part.archivedAt === null
                          ? t("prop.mortgage.archive")
                          : t("prop.mortgage.restore"),
                      destructive: part.archivedAt === null,
                      disabled: archive.isPending,
                      onSelect: () => {
                        archive.mutate({
                          partId: part.id,
                          verb: part.archivedAt === null ? "archive" : "unarchive",
                        });
                      },
                    },
                  ]}
                />
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      {archived.length > 0 ? (
        <div>
          <Button
            onClick={() => {
              setShowArchived((open) => !open);
            }}
          >
            {showArchived ? t("prop.hide") : t("prop.show")} {archived.length} archived{" "}
            {archived.length === 1 ? "part" : "parts"}
          </Button>
          {/*
            Revealing them is a DISCLOSURE, not a filter. The "still owed" figure above counts active
            parts only and must not move when this is expanded — which is why the total is computed
            from `active` rather than from whatever the table happens to be rendering.
          */}
        </div>
      ) : null}
    </div>
  );
}

export type MortgagePartRow = PropertyDetailDto["mortgageParts"][number];
